import {
  convertFirebaseDateToLocal,
  getPartnerIdFromChatId,
} from "@/firebase/utils";
import { Profile } from "@/store/auth/state";
import {
  OneToOneRawTextMessage,
  ChatTypes,
  MessageType,
  TextMessage,
  OneToOneRawChat,
} from "@/store/chats/types";
import { User } from "@/store/users/state";
import { getNonAutomaticReplyMessage, parseMessageText } from ".";
import { getDisplayOneToOneMessage } from "../chat/getDisplayOneToOneMessage";
import { getImageAttachments } from "../chat/getImageAttachments";
import { getOneToOneMessageReadBy } from "../chat/getOneToOneMessageReadBy";
import { getOthersAttachments } from "../chat/getOthersAttachments";
import { getReadOneToOneMessage } from "../chat/getReadOneToOneMessage";
import { getSenderName } from "../chat/getSenderName";
import { getStatusIcon } from "../chat/getStatusIcon";
import { getVideoAttachments } from "../chat/getVideoAttachments";
import { getVisibleAttachments } from "./getVisibleAttachments";
import { findUserById } from "../users";

interface ConvertRawOneToOneTextMessageArgs {
  rawMessage: OneToOneRawTextMessage;
  rawChat: OneToOneRawChat;
  auth: Profile;
  users?: User[];
  ignoreReplies: boolean;
}

export const convertRawOneToOneTextMessage = ({
  rawMessage,
  rawChat,
  auth,
  users,
  ignoreReplies,
}: ConvertRawOneToOneTextMessageArgs): TextMessage => {
  const {
    senderId,
    messageText,
    attachments,
    hide,
    sentAt,
    id,
    reactions,
    replyToMessage,
    isModified,
    isDeleted,
    isForwarded,
  } = rawMessage;
  const { id: chatId } = rawChat;
  const visibleAttachments = getVisibleAttachments(attachments);
  const partnerId = getPartnerIdFromChatId(auth.id, chatId);
  const partner = findUserById(partnerId, users);
  const members = [partner].filter(Boolean) as User[];
  const hasAttachment = Boolean(attachments && attachments.length);
  const hasMessageText = !!messageText;
  const hidden = Boolean(hide) && !(hasMessageText || hasAttachment);

  return {
    chatType: ChatTypes.ONE_TO_ONE_CHAT,
    id,
    senderId,
    senderName: getSenderName(senderId, auth.id, members),
    isMine: auth.id === senderId,
    messageText,
    displayMessageText: getDisplayOneToOneMessage(rawMessage),
    parseMessageText: parseMessageText(messageText),
    sentAt: convertFirebaseDateToLocal(sentAt),
    hasAttachment,
    hasMessageText,
    hidden,
    readBy: getOneToOneMessageReadBy(rawMessage, auth, chatId),
    read: getReadOneToOneMessage(rawMessage, auth),
    statusIcon: getStatusIcon(rawMessage, auth),
    attachments: visibleAttachments,
    allAttachments: attachments,
    imageAttachments: getImageAttachments(visibleAttachments),
    videoAttachments: getVideoAttachments(visibleAttachments),
    othersAttachments: getOthersAttachments(visibleAttachments),
    type: MessageType.Text,
    reactions,
    replyToMessage: getNonAutomaticReplyMessage({
      rawMessage: replyToMessage,
      rawChat,
      users,
      ignoreReplies,
    }),
    isModified,
    isDeleted,
    isForwarded,
  };
};
